// Minimal IndexedDB key/value store for offline drafts, pending mutations and upload records.
// Every key is prefixed with the owner scope `user_id:business_id` (§11c).

const DB = 'workdesk';
const STORE = 'kv';
let dbp: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') return Promise.reject(new Error('no indexedDB'));
  return (dbp ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbp = null;
      reject(req.error);
    };
  }));
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const r = fn(t.objectStore(STORE));
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
      }),
  );
}

export const scopeKey = (userId: string, businessId: string) => `${userId}:${businessId}`;

export const idbGet = <T>(key: string) => tx<T | undefined>('readonly', (s) => s.get(key) as IDBRequest<T | undefined>).catch(() => undefined);
export const idbSet = (key: string, value: unknown) => tx('readwrite', (s) => s.put(value, key)).then(() => undefined).catch(() => undefined);
export const idbDel = (key: string) => tx('readwrite', (s) => s.delete(key)).then(() => undefined).catch(() => undefined);

export async function idbKeys(prefix: string): Promise<string[]> {
  try {
    const keys = await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys());
    return keys.map(String).filter((k) => k.startsWith(prefix));
  } catch {
    return [];
  }
}

/** Logout: drop every store (§11c). */
export async function idbWipe(): Promise<void> {
  try {
    const db = await dbp;
    db?.close();
  } catch {
    /* ignore */
  }
  dbp = null;
  if (typeof indexedDB === 'undefined') return;
  await new Promise<void>((resolve) => {
    const r = indexedDB.deleteDatabase(DB);
    r.onsuccess = r.onerror = r.onblocked = () => resolve();
  });
}
